import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, InputBase, Menu, MenuItem, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { biedOngedaanAan } from '../../../utils/ongedaan';
import { T, Icon } from '../parts/stitch';
import { useProjectData } from '../ProjectDataContext';
import {
  defVan, foutUit, gewijzigdNaDef, klapGelijkIn, korteDatum, mijlpaalLabel, mijlpalenPerItem, verschilZin,
  versieZin, wordAppUrl,
} from './scriptWeergave';

/**
 * Scriptpaneel (scripttool uit SharePoint, fase 1 + 2 — 3 nov 2026; ontwerp "Scripttool uit SharePoint").
 *
 * SharePoint is de bron: de tool leest het laatste script live, bewerken gaat in Word. Eén focuspunt (het
 * laatste script), één accentkleur; versies, mijlpalen, andere scripts en koppelen staan stil eronder of
 * ingeklapt. Mijlpalen (v1/v2/DEF) zijn tekstmomentopnamen; vastleggen en ontkoppelen hebben undo.
 * Identiek op develop (voordeur) en main (Node).
 */

const ACCENT = T.tertiary;
const labelSx = {
  fontFamily: T.font, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em',
  textTransform: 'uppercase', color: T.outline,
};
const knopSx = {
  border: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.75,
  bgcolor: ACCENT, color: '#fff', borderRadius: '10px', px: 2, py: 1, textDecoration: 'none',
  fontFamily: T.font, fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.2,
  '&:hover': { bgcolor: '#564d74' },
  '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
  '&:disabled': { opacity: 0.6, cursor: 'default' },
};
const knop2Sx = {
  ...knopSx, bgcolor: 'transparent', color: T.text, border: `1px solid ${T.surfaceHigh}`,
  '&:hover': { borderColor: T.outlineVariant },
};
const linkSx = {
  border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', textAlign: 'left',
  fontFamily: T.fontBody, fontSize: '0.85rem', color: T.secondary, textDecoration: 'none',
  '&:hover': { color: ACCENT },
  '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2, borderRadius: '4px' },
  '&:disabled': { opacity: 0.6, cursor: 'default' },
};
const stilSx = { fontFamily: T.fontBody, fontSize: '0.82rem', color: T.outline };

const api = (nr, pad = '') => `${BACKEND_URL}/Api/project-scripts/${encodeURIComponent(nr)}${pad}`;

async function json(r, standaard) {
  if (!r.ok) throw new Error(await foutUit(r, standaard));
  return r.json();
}

function ScriptPanel() {
  const { projectNumber, project } = useProjectData();
  const nr = String(projectNumber || '');
  const klantRaw = project?.fields?.KlantnaamString;
  const klant = Array.isArray(klantRaw) ? (klantRaw[0] || '') : (klantRaw || '');
  const kq = klant ? `klant=${encodeURIComponent(klant)}` : '';

  const [data, setData] = useState(null);
  const [laden, setLaden] = useState(false);
  const [fout, setFout] = useState(null);
  const [reeksSleutel, setReeksSleutel] = useState(null);
  const [itemKeuze, setItemKeuze] = useState(null);
  const [tekst, setTekst] = useState(null);          // { item, blokken, etag, ... }
  const [tekstFout, setTekstFout] = useState(null);
  const [weergave, setWeergave] = useState('tekst'); // tekst | verschil | mijlpaal
  const [verschil, setVerschil] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [menu, setMenu] = useState(null);            // { anker, versie } | { anker, mijlpaal }
  const [open, setOpen] = useState({ mijlpalen: false, andere: false, koppelen: false });
  const [link, setLink] = useState('');
  const [uitgeklapt, setUitgeklapt] = useState({});
  const [tekstRonde, setTekstRonde] = useState(0);   // expliciet verversen / conflict: tekst opnieuw ophalen

  // Alles is gebonden aan het project waarvoor het startte (projectwissel tijdens laden/undo = negeren).
  // Undo mag alleen zolang dit paneel voor dít project open staat (Codex code r1/r2): elke projectwissel en
  // elk vertrek verhoogt de generatie en ruimt de openstaande undo-toasts op; A → B → A maakt een oude undo
  // dus niet opnieuw geldig.
  const actueel = useRef(nr);
  const generatie = useRef(0);
  const gemount = useRef(true);
  const undoToasts = useRef([]);
  const ruimUndoOp = () => {
    undoToasts.current.forEach((id) => toast.dismiss(id));
    undoToasts.current = [];
  };
  useEffect(() => {
    actueel.current = nr;
    generatie.current += 1;
    ruimUndoOp();
    setBezig(false);     // een lopende actie van het vorige project blokkeert de knoppen hier niet (Codex code r7)
  }, [nr]);
  useEffect(() => {
    gemount.current = true;
    return () => { gemount.current = false; generatie.current += 1; ruimUndoOp(); };
  }, []);
  const magNog = (voor, gen) => gemount.current && actueel.current === voor && (gen === undefined || generatie.current === gen);
  const bied = (tekst, voor, gen, herstel) => {
    const id = biedOngedaanAan(tekst, async () => {
      if (!magNog(voor, gen)) throw new Error('je bent intussen naar een ander project gegaan; er is niets teruggezet');
      await herstel();
      if (magNog(voor, gen)) await laad();
    }, { eigenKnop: true });
    if (id != null) undoToasts.current.push(id);
  };

  // Alleen het laatst gestarte laadverzoek van deze projectgeneratie mag de staat zetten (Codex code r5:
  // een trager antwoord van een eerder bezoek overschrijft anders het nieuwe overzicht).
  const laadNr = useRef(0);
  const laad = useCallback(async (ververs = false) => {
    if (!nr || actueel.current !== nr) return;     // een oude closure (vorig project) laadt niets meer
    const voor = nr;
    const gen = generatie.current;
    const mijn = ++laadNr.current;
    const actueelVerzoek = () => laadNr.current === mijn && actueel.current === voor && generatie.current === gen;
    setLaden(true);
    setFout(null);
    try {
      const q = [kq, ververs ? 'ververs=1' : ''].filter(Boolean).join('&');
      const d = await json(await authFetch(api(voor, q ? `?${q}` : '')), 'Scripts ophalen lukte niet');
      if (!actueelVerzoek()) return;
      setData(d);
      if (ververs) setTekstRonde((n) => n + 1);
      setReeksSleutel((oud) => (d.reeksen.some((r) => r.sleutel === oud) ? oud : d.reeksen[0]?.sleutel || null));
    } catch (e) {
      if (actueelVerzoek()) setFout(e.message);
    } finally {
      if (actueelVerzoek()) setLaden(false);
    }
  }, [nr, kq]);
  useEffect(() => { setData(null); setTekst(null); setItemKeuze(null); setWeergave('tekst'); laad(); }, [laad]);

  const reeks = useMemo(() => data?.reeksen?.find((r) => r.sleutel === reeksSleutel) || null, [data, reeksSleutel]);
  const versie = useMemo(() => {
    if (!reeks) return null;
    return reeks.versies.find((v) => v.item === itemKeuze) || reeks.versies[0];
  }, [reeks, itemKeuze]);
  const mijlpalen = useMemo(() => data?.mijlpalen || [], [data]);
  const perItem = useMemo(() => mijlpalenPerItem(mijlpalen), [mijlpalen]);
  const def = reeks ? defVan(mijlpalen, reeks.sleutel) : null;
  const vorige = reeks && versie ? reeks.versies[reeks.versies.indexOf(versie) + 1] : null;

  // Tekst van de gekozen versie (opnieuw bij een andere versie of een gewijzigd bestand, niet bij elke herlaad).
  const versieSleutel = versie ? `${versie.item}|${versie.etag || ''}` : '';
  useEffect(() => {
    if (!versieSleutel || !nr) return undefined;
    const voor = nr;
    const item = versieSleutel.split('|')[0];
    let weg = false;
    setTekst(null);
    setTekstFout(null);
    authFetch(api(voor, `/tekst?item=${encodeURIComponent(item)}${kq ? `&${kq}` : ''}`))
      .then((r) => json(r, 'De tekst kon niet worden gelezen'))
      .then((t) => { if (!weg && actueel.current === voor) setTekst(t); })
      .catch((e) => { if (!weg && actueel.current === voor) setTekstFout(e.message); });
    return () => { weg = true; };
  }, [versieSleutel, nr, kq, tekstRonde]);

  // Leesacties (vergelijken, momentopname): alleen het laatst gekozen verzoek van deze projectgeneratie telt;
  // een andere versie, reeks of "Terug naar tekst" maakt lopende leesacties ongeldig (Codex code r6).
  const leesNr = useRef(0);
  const naarTekst = () => { leesNr.current += 1; setWeergave('tekst'); };
  const kiesVersie = (v) => { leesNr.current += 1; setItemKeuze(v.item); setWeergave('tekst'); setVerschil(null); };

  const vergelijk = async (v = versie) => {
    if (!v) return;
    const voor = nr;
    const gen = generatie.current;
    const mijn = ++leesNr.current;
    const geldig = () => leesNr.current === mijn && magNog(voor, gen);
    setBezig(true);
    try {
      const d = await json(await authFetch(api(voor, `/vergelijk?item=${encodeURIComponent(v.item)}${kq ? `&${kq}` : ''}`)),
        'Vergelijken lukte niet');
      if (!geldig()) return;
      setItemKeuze(v.item);
      setVerschil(d);
      setUitgeklapt({});
      setWeergave('verschil');
    } catch (e) {
      if (geldig()) toast.error(e.message);
    } finally {
      if (magNog(voor, gen)) setBezig(false);
    }
  };

  const markeer = async (soort, v = versie) => {
    if (!v) return;
    const voor = nr;
    const gen = generatie.current;     // vastgelegd bij de start: na A → B → A geen undo voor een oude actie
    const etag = tekst?.item === v.item && tekst?.etag ? tekst.etag : v.etag;
    setBezig(true);
    try {
      const d = await json(await authFetch(api(voor, `/mijlpalen${kq ? `?${kq}` : ''}`), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item: v.item, soort, etag }),
      }), 'Vastleggen lukte niet');
      if (!magNog(voor, gen)) return;
      const id = d?.mijlpaal?.id;
      await laad();
      if (id && magNog(voor, gen)) {
        bied(`${mijlpaalLabel(soort)} vastgelegd: ${v.versie}`, voor, gen, async () => {
          await json(await authFetch(api(voor, `/mijlpalen/${id}/ongedaan`), { method: 'POST' }), 'Ongedaan maken lukte niet');
        });
      }
    } catch (e) {
      if (!magNog(voor, gen)) return;     // antwoord van een vorig projectbezoek: niets meer doen (Codex code r6)
      toast.error(e.message);
      if (/gewijzigd/i.test(e.message)) laad(true);   // ververst ook de tekst (tekstRonde)
    } finally {
      if (magNog(voor, gen)) setBezig(false);
    }
  };

  const trekIn = async (m) => {
    const voor = nr;
    const gen = generatie.current;
    try {
      await json(await authFetch(api(voor, `/mijlpalen/${m.id}`), { method: 'DELETE' }), 'Intrekken lukte niet');
      if (!magNog(voor, gen)) return;
      await laad();
      if (!magNog(voor, gen)) return;
      if (weergave === 'mijlpaal' && snapshot?.id === m.id) naarTekst();
      bied(`Mijlpaal ${mijlpaalLabel(m.soort)} ingetrokken`, voor, gen, async () => {
        await json(await authFetch(api(voor, `/mijlpalen/${m.id}/herstel`), { method: 'POST' }), 'Terugzetten lukte niet');
      });
    } catch (e) {
      if (magNog(voor, gen)) toast.error(e.message);
    }
  };

  const openSnapshot = async (m) => {
    const voor = nr;
    const gen = generatie.current;
    const mijn = ++leesNr.current;
    const geldig = () => leesNr.current === mijn && magNog(voor, gen);
    try {
      const d = await json(await authFetch(api(voor, `/mijlpalen/${m.id}`)), 'Momentopname ophalen lukte niet');
      if (!geldig()) return;
      setSnapshot(d);
      setWeergave('mijlpaal');
    } catch (e) {
      if (geldig()) toast.error(e.message);
    }
  };

  const koppel = async () => {
    const url = link.trim();
    if (!url) return;
    const voor = nr;
    const gen = generatie.current;
    setBezig(true);
    try {
      await json(await authFetch(api(voor, '/koppeling'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url }),
      }), 'Koppelen lukte niet');
      if (!magNog(voor, gen)) return;
      setLink('');
      toast.success('Link gekoppeld');
      await laad(true);
    } catch (e) {
      if (magNog(voor, gen)) toast.error(e.message);
    } finally {
      if (magNog(voor, gen)) setBezig(false);
    }
  };

  const ontkoppel = async () => {
    const voor = nr;
    const gen = generatie.current;
    try {
      const d = await json(await authFetch(api(voor, '/koppeling'), { method: 'DELETE' }), 'Ontkoppelen lukte niet');
      if (!magNog(voor, gen)) return;
      await laad(true);
      const oud = d?.oud?.url;
      if (oud && magNog(voor, gen)) {
        bied('Link ontkoppeld', voor, gen, async () => {
          await json(await authFetch(api(voor, '/koppeling/herstel'), {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: oud }),
          }), 'Terugzetten lukte niet');
        });
      }
    } catch (e) {
      if (magNog(voor, gen)) toast.error(e.message);
    }
  };

  if (!nr) {
    return <Typography sx={stilSx}>Dit project heeft nog geen projectnummer; daarmee zoekt de tool het script.</Typography>;
  }
  if (laden && !data) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4 }}>
        <CircularProgress size={16} sx={{ color: ACCENT }} />
        <Typography sx={stilSx}>Scripts zoeken in SharePoint…</Typography>
      </Box>
    );
  }
  if (fout && !data) {
    return (
      <Box sx={{ py: 3 }}>
        <Typography sx={{ ...stilSx, color: T.text, mb: 1 }}>{fout}</Typography>
        <Box component="button" type="button" onClick={() => laad(true)} sx={linkSx}>Opnieuw proberen</Box>
      </Box>
    );
  }

  const andere = (data?.reeksen || []).filter((r) => r.sleutel !== reeksSleutel);
  const reeksMijlpalen = reeks ? mijlpalen.filter((m) => m.reeks === reeks.sleutel) : [];
  const andereMijlpalen = mijlpalen.filter((m) => !reeks || m.reeks !== reeks.sleutel);
  const isDefNu = def && versie && def.item === versie.item && (!def.etag || def.etag === (tekst?.etag || versie.etag));
  const wordApp = wordAppUrl(versie?.webUrl);
  // Een momentopname is ook leesbaar als het bronbestand er niet meer is (Codex code r1).
  const snapshotBlok = weergave === 'mijlpaal' && snapshot ? (
    <>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={labelSx}>
          Momentopname {mijlpaalLabel(snapshot.soort)} · {snapshot.bestandsnaam} · {korteDatum(snapshot.vastgelegdOp)}
        </Typography>
        <Box component="button" type="button" onClick={naarTekst} sx={linkSx}>
          {reeks ? 'Terug naar tekst' : 'Sluiten'}
        </Box>
      </Box>
      <Box sx={leesvakSx}>
        {(snapshot.regels || []).map((r, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <Typography key={i} sx={{ ...regelSx, minHeight: '0.9em' }}>{r}</Typography>
        ))}
      </Box>
    </>
  ) : null;

  return (
    <Box sx={{ display: 'grid', gap: 3.5, pb: 4 }}>
      {fout && (
        <Typography sx={stilSx}>
          Verversen lukte niet ({fout}); wat je ziet is niet bijgewerkt.{' '}
          <Box component="button" type="button" onClick={() => laad(true)} sx={{ ...linkSx, fontSize: '0.82rem' }}>Opnieuw</Box>
        </Typography>
      )}
      {(data?.meldingen || []).map((m) => <Typography key={m} sx={stilSx}>{m}</Typography>)}

      {!reeks ? (
        <Box sx={{ display: 'grid', gap: 1 }}>
          <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '1rem', color: T.text }}>
            Geen script gevonden in de projectmappen op SharePoint
          </Typography>
          <Typography sx={stilSx}>
            De tool zoekt op projectnummer {nr} in 01. Opdrachten (Intranet) en FF Klanten, in de mappen met
            &lsquo;Script&rsquo; of &lsquo;Aangeleverd&rsquo;. Staat het script ergens anders, koppel dan hieronder de map of het bestand.
          </Typography>
          {snapshotBlok && <Box sx={{ mt: 2, display: 'grid', gap: 1.5 }}>{snapshotBlok}</Box>}
        </Box>
      ) : (
        <>
          {/* Het laatste script: het enige focuspunt */}
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box sx={{ display: 'grid', gap: 0.5, minWidth: 0 }}>
              <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: '1.08rem', color: T.text, overflowWrap: 'anywhere' }}>
                {versie?.naam}
              </Typography>
              <Typography sx={{ ...stilSx, color: T.secondary }}>
                {versieZin(versie)}{versie && reeks.versies[0] !== versie ? ' · niet de laatste versie' : ''}
              </Typography>
              {versie?.map && <Typography sx={{ ...stilSx, fontSize: '0.76rem', overflowWrap: 'anywhere' }}>{versie.map}</Typography>}
              {reeks.aangeleverd && <Typography sx={stilSx}>Aangeleverd door de klant</Typography>}
              {def && (
                <Typography sx={stilSx}>
                  DEF: {def.versie || def.bestandsnaam} · vastgelegd {korteDatum(def.vastgelegdOp)}
                  {def.vastgelegdDoor ? ` door ${def.vastgelegdDoor.split('@')[0]}` : ''}
                  {gewijzigdNaDef(def, reeks, tekst) ? ' · het script is daarna nog gewijzigd' : ''}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25 }}>
              {versie?.webUrl && (
                <Box component="a" href={versie.webUrl} target="_blank" rel="noopener noreferrer" sx={knopSx}>
                  Open in Word
                </Box>
              )}
              {wordApp && <Box component="a" href={wordApp} sx={linkSx}>in de Word-app</Box>}
              {vorige && (
                <Box component="button" type="button" disabled={bezig} onClick={() => vergelijk()} sx={knop2Sx}>
                  Vergelijk met vorige versie
                </Box>
              )}
              {isDefNu ? (
                <Typography sx={{ ...stilSx, color: ACCENT, fontWeight: 600 }}>DEF</Typography>
              ) : (
                <Box component="button" type="button" disabled={bezig || !tekst} onClick={() => markeer('def')} sx={knop2Sx}>
                  Markeer als DEF
                </Box>
              )}
            </Box>
          </Box>

          {/* Tekst, verschil of momentopname */}
          <Box sx={{ borderTop: `1px solid ${T.surfaceHigh}`, pt: 2, display: 'grid', gap: 1.5, minWidth: 0 }}>
            {weergave === 'verschil' && verschil ? (
              <VerschilWeergave verschil={verschil} uitgeklapt={uitgeklapt} setUitgeklapt={setUitgeklapt}
                onTerug={naarTekst} />
            ) : snapshotBlok || (
              <>
                <Typography sx={labelSx}>Tekstweergave · alleen lezen</Typography>
                {tekstFout ? (
                  <Typography sx={stilSx}>{tekstFout}</Typography>
                ) : !tekst ? (
                  <Box sx={{ py: 2 }}><CircularProgress size={14} sx={{ color: T.outlineVariant }} /></Box>
                ) : (
                  <TekstWeergave tekst={tekst} />
                )}
              </>
            )}
          </Box>

          {/* Versies */}
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Typography sx={labelSx}>Versies</Typography>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {reeks.versies.map((v) => {
                const gekozen = v === versie;
                const labels = perItem[v.item] || [];
                return (
                  <Box component="li" key={v.item} sx={{
                    display: 'flex', alignItems: 'center', gap: 1, py: 0.9, borderTop: `1px solid ${T.surfaceLow}`,
                    '&:first-of-type': { borderTop: 0 },
                  }}>
                    <Box component="button" type="button" onClick={() => kiesVersie(v)} sx={{ ...linkSx, flex: 1, minWidth: 0 }}>
                      <Box component="span" sx={{ fontFamily: T.font, fontWeight: 700, color: gekozen ? ACCENT : T.text, mr: 1 }}>
                        {v.versie}
                      </Box>
                      <Box component="span" sx={{ color: T.outline, fontSize: '0.8rem' }}>
                        {korteDatum(v.gewijzigdOp)}{v.gewijzigdDoor ? ` · ${v.gewijzigdDoor}` : ''}
                        {labels.length ? ` · mijlpaal ${labels.join(', ')}` : ''}
                      </Box>
                    </Box>
                    <Box component="button" type="button" aria-label={`Meer bij ${v.versie}`}
                      onClick={(e) => setMenu({ anker: e.currentTarget, versie: v })}
                      sx={{ ...linkSx, color: T.outlineVariant, display: 'inline-flex' }}>
                      <Icon name="more_horiz" size={18} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </>
      )}

      {/* Mijlpalen (ook als het bronbestand verdwenen is) */}
      {mijlpalen.length > 0 && (
        <Inklap titel={`Mijlpalen (${mijlpalen.length})`} open={open.mijlpalen}
          onWissel={() => setOpen((o) => ({ ...o, mijlpalen: !o.mijlpalen }))}>
          {[...reeksMijlpalen, ...andereMijlpalen].map((m) => (
            <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6 }}>
              <Box component="button" type="button" onClick={() => openSnapshot(m)} sx={{ ...linkSx, flex: 1, minWidth: 0 }}>
                <Box component="span" sx={{ fontFamily: T.font, fontWeight: 700, color: T.text, mr: 1 }}>{mijlpaalLabel(m.soort)}</Box>
                <Box component="span" sx={{ color: T.outline, fontSize: '0.8rem' }}>
                  {m.bestandsnaam} · {korteDatum(m.vastgelegdOp)}{m.automatisch ? ' · automatisch' : ''}
                </Box>
              </Box>
              <Box component="button" type="button" aria-label={`Meer bij mijlpaal ${mijlpaalLabel(m.soort)}`}
                onClick={(e) => setMenu({ anker: e.currentTarget, mijlpaal: m })}
                sx={{ ...linkSx, color: T.outlineVariant, display: 'inline-flex' }}>
                <Icon name="more_horiz" size={18} />
              </Box>
            </Box>
          ))}
        </Inklap>
      )}

      {andere.length > 0 && (
        <Inklap titel={`Andere scripts in dit project (${andere.length})`} open={open.andere}
          onWissel={() => setOpen((o) => ({ ...o, andere: !o.andere }))}>
          {andere.map((r) => (
            <Box key={r.sleutel} component="button" type="button"
              onClick={() => { leesNr.current += 1; setReeksSleutel(r.sleutel); setItemKeuze(null); setWeergave('tekst'); }}
              sx={{ ...linkSx, display: 'block', py: 0.6 }}>
              <Box component="span" sx={{ color: T.text }}>{r.versies[0]?.naam}</Box>
              <Box component="span" sx={{ color: T.outline, fontSize: '0.8rem' }}>
                {' '}· {r.versies[0]?.versie}{r.aangeleverd ? ' · aangeleverd' : ''}
              </Box>
            </Box>
          ))}
        </Inklap>
      )}

      <Inklap titel={reeks ? 'Niet het goede script? Koppel een SharePoint-link' : 'Koppel een SharePoint-link'}
        open={open.koppelen || !reeks} onWissel={() => setOpen((o) => ({ ...o, koppelen: !o.koppelen }))}>
        {data?.koppeling && (
          <Typography sx={{ ...stilSx, mb: 1 }}>
            Gekoppeld: {data.koppeling.naam || data.koppeling.url}{' · '}
            <Box component="button" type="button" onClick={ontkoppel} sx={{ ...linkSx, fontSize: '0.82rem' }}>Ontkoppel</Box>
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <InputBase value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://fullframe.sharepoint.com/…"
            inputProps={{ 'aria-label': 'SharePoint-link' }}
            onKeyDown={(e) => { if (e.key === 'Enter') koppel(); }}
            sx={{ flex: 1, minWidth: 0, fontFamily: T.fontBody, fontSize: '0.85rem', px: 1.25, py: 0.5,
              border: `1px solid ${T.surfaceHigh}`, borderRadius: '8px' }} />
          <Box component="button" type="button" disabled={bezig || !link.trim()} onClick={koppel} sx={knop2Sx}>Koppel</Box>
        </Box>
        <Typography sx={{ ...stilSx, mt: 1, fontSize: '0.76rem' }}>
          Koppel de projectmap zelf, of een map of script daarin. Scripts in een persoonlijke OneDrive (bijvoorbeeld via
          een Teams-chat gedeeld) worden niet ondersteund, ook niet via een link: zet het bestand in de projectmap.
        </Typography>
      </Inklap>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Box component="button" type="button" disabled={laden} onClick={() => laad(true)} sx={{ ...linkSx, fontSize: '0.8rem', color: T.outline }}>
          {laden ? 'Verversen…' : 'Verversen'}
        </Box>
        {(data?.mappen || []).filter((m) => m.webUrl).slice(0, 3).map((m) => (
          <Box key={m.webUrl} component="a" href={m.webUrl} target="_blank" rel="noopener noreferrer"
            sx={{ ...linkSx, fontSize: '0.8rem', color: T.outline }}>
            Map openen{(data?.mappen || []).length > 1 ? `: ${m.naam}` : ''}
          </Box>
        ))}
      </Box>

      <Menu anchorEl={menu?.anker} open={!!menu} onClose={() => setMenu(null)}
        slotProps={{ paper: { sx: { borderRadius: '0.75rem', minWidth: 200 } } }}>
        {menu?.versie && [
          { k: 'v1', label: 'Vastleggen als v1', actie: () => markeer('v1', menu.versie) },
          { k: 'v2', label: 'Vastleggen als v2', actie: () => markeer('v2', menu.versie) },
          { k: 'def', label: 'Markeer als DEF', actie: () => markeer('def', menu.versie) },
          { k: 'verg', label: 'Vergelijk met vorige versie', actie: () => vergelijk(menu.versie),
            uit: reeks?.versies?.indexOf(menu.versie) === reeks?.versies?.length - 1 },
        ].filter((x) => !x.uit).map((x) => (
          <MenuItem key={x.k} onClick={() => { setMenu(null); x.actie(); }} sx={{ fontFamily: T.fontBody, fontSize: '0.86rem' }}>
            {x.label}
          </MenuItem>
        ))}
        {menu?.mijlpaal && [
          <MenuItem key="open" onClick={() => { const m = menu.mijlpaal; setMenu(null); openSnapshot(m); }}
            sx={{ fontFamily: T.fontBody, fontSize: '0.86rem' }}>Momentopname lezen</MenuItem>,
          <MenuItem key="trek" onClick={() => { const m = menu.mijlpaal; setMenu(null); trekIn(m); }}
            sx={{ fontFamily: T.fontBody, fontSize: '0.86rem' }}>Mijlpaal intrekken</MenuItem>,
        ]}
      </Menu>
    </Box>
  );
}

const leesvakSx = {
  maxHeight: '58vh', overflow: 'auto', pr: 1, display: 'grid', gap: 1.25,
  fontFamily: T.fontBody, fontSize: '0.9rem', lineHeight: 1.6, color: T.text,
};
const regelSx = { fontFamily: T.fontBody, fontSize: '0.9rem', lineHeight: 1.6, color: T.text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' };

function TekstWeergave({ tekst }) {
  if (!tekst.blokken?.length) return <Typography sx={stilSx}>Dit document bevat geen leesbare tekst.</Typography>;
  return (
    <Box sx={leesvakSx}>
      {tekst.blokken.map((b, i) => (b.soort === 'tabel' ? (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={i} sx={{ display: 'grid', gap: 1, overflowX: 'auto' }}>
          {b.rijen.map((rij, j) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={j} sx={{ display: 'grid', gap: 2, gridTemplateColumns: `repeat(${rij.length}, minmax(${rij.length > 4 ? '9rem' : '0'}, 1fr))` }}>
              {rij.map((cel, k) => (
                // eslint-disable-next-line react/no-array-index-key
                <Typography key={k} sx={{ ...regelSx, color: k === 0 && rij.length > 2 ? T.outline : T.text }}>{cel}</Typography>
              ))}
            </Box>
          ))}
        </Box>
      ) : (
        // eslint-disable-next-line react/no-array-index-key
        <Typography key={i} sx={{ ...regelSx, ...(b.kop ? { fontFamily: T.font, fontWeight: 700 } : {}) }}>{b.tekst}</Typography>
      )))}
      {tekst.afgekapt && <Typography sx={stilSx}>De rest van het document staat alleen in Word.</Typography>}
    </Box>
  );
}

const verschilKleur = {
  weg: { bgcolor: '#fbefef', color: '#7a4a48', textDecoration: 'line-through', textDecorationColor: '#c9a3a1' },
  erbij: { bgcolor: '#eef7f1', color: T.text },
};

function VerschilWeergave({ verschil, uitgeklapt, setUitgeklapt, onTerug }) {
  const stukken = klapGelijkIn(verschil.stukken || []);
  const oudLabel = verschil.oud?.mijlpaal ? `momentopname ${mijlpaalLabel(verschil.oud.mijlpaal)}` : verschil.oud?.versie;
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={labelSx}>{verschil.nieuw?.versie} vergeleken met {oudLabel}</Typography>
        <Typography sx={stilSx}>{verschilZin(verschil.telling, verschil.afgekapt)}</Typography>
        <Box component="button" type="button" onClick={onTerug} sx={linkSx}>Terug naar tekst</Box>
      </Box>
      {verschil.grof && <Typography sx={stilSx}>Grof vergeleken: het script is erg lang.</Typography>}
      {verschil.afgekapt && (
        <Typography sx={stilSx}>Alleen het begin is vergeleken: het script is langer dan de tool leest. De rest staat in Word.</Typography>
      )}
      <Box sx={{ ...leesvakSx, gap: 0.25 }}>
        {stukken.map((s, i) => {
          if (s.soort === 'ingeklapt') {
            if (uitgeklapt[i]) {
              return s.regels.map((r, j) => (
                // eslint-disable-next-line react/no-array-index-key
                <Typography key={`${i}-${j}`} sx={{ ...regelSx, color: T.secondary }}>{r}</Typography>
              ));
            }
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={i} component="button" type="button" onClick={() => setUitgeklapt((u) => ({ ...u, [i]: true }))}
                sx={{ ...linkSx, fontSize: '0.8rem', color: T.outline, py: 0.5 }}>
                … {s.aantal} {s.aantal === 1 ? 'regel' : 'regels'} ongewijzigd
              </Box>
            );
          }
          if (s.soort === 'gewijzigd') {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <Typography key={i} sx={{ ...regelSx, px: 0.75, borderRadius: '4px' }}>
                {(s.delen || []).map((d, j) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Box key={j} component="span" sx={d.t === '=' ? {} : { ...verschilKleur[d.t === '-' ? 'weg' : 'erbij'], borderRadius: '3px' }}>
                    {d.s}
                  </Box>
                ))}
              </Typography>
            );
          }
          return (s.regels || []).map((r, j) => (
            // eslint-disable-next-line react/no-array-index-key
            <Typography key={`${i}-${j}`} sx={{ ...regelSx, px: 0.75, borderRadius: '4px', ...(verschilKleur[s.soort] || { color: T.secondary }) }}>
              {r}
            </Typography>
          ));
        })}
      </Box>
    </>
  );
}

function Inklap({ titel, open, onWissel, children }) {
  return (
    <Box>
      <Box component="button" type="button" onClick={onWissel} aria-expanded={open}
        sx={{ ...linkSx, display: 'inline-flex', alignItems: 'center', gap: 0.5, color: T.secondary }}>
        <Icon name={open ? 'expand_less' : 'expand_more'} size={18} />
        {titel}
      </Box>
      {open && <Box sx={{ mt: 1, pl: 3 }}>{children}</Box>}
    </Box>
  );
}

export default ScriptPanel;
